import http from "../../../../../api/apiClient";
import { createLdsMemoryCache, createLdsRequestCache } from "../../../../../components/lds/cache";
import type {
  ChildSummary,
  DecoratedMutationRequest,
  EntityId,
  EntityLookupKind,
  FamilySummary,
  LookupEntry,
  LookupMap,
  MutationRequest,
  MutationRequestAction,
  MutationRequestCollection,
  ParentSummary,
  UserSummary,
} from "../types/mutationRequests.types";
import {
  childSummaryFromResponse,
  decorateRequest,
  fallbackLookupEntry,
  familySummaryFromResponse,
  parentSummaryFromResponse,
  toMutationRequestCollection,
  userSummaryFromResponse,
} from "../utils/mutationRequests.utils";

const API_PATH = "/mutation-requests";
const CACHE_TTL_MS = 60_000;

interface MutationRequestsCacheEntry {
  data: MutationRequestCollection;
  updatedAt: number;
}

const requestsCache = createLdsMemoryCache<MutationRequestsCacheEntry>();
const loadRequestsOnce = createLdsRequestCache<MutationRequestCollection>();

const entityCaches: {
  user: Map<EntityId, UserSummary>;
  parent: Map<EntityId, ParentSummary>;
  child: Map<EntityId, ChildSummary>;
  family: Map<EntityId, FamilySummary>;
} = {
  user: new Map(),
  parent: new Map(),
  child: new Map(),
  family: new Map(),
};

const entityRequests: {
  user: Map<EntityId, Promise<UserSummary>>;
  parent: Map<EntityId, Promise<ParentSummary>>;
  child: Map<EntityId, Promise<ChildSummary>>;
  family: Map<EntityId, Promise<FamilySummary>>;
} = {
  user: new Map(),
  parent: new Map(),
  child: new Map(),
  family: new Map(),
};

const normalizeMutationRequests = (data: unknown): MutationRequest[] => {
  if (!Array.isArray(data)) return [];
  return data.filter((item): item is MutationRequest => {
    if (!item || typeof item !== "object") return false;
    return typeof (item as Partial<MutationRequest>).id === "string";
  });
};

export const hasMutationRequestsCache = () => {
  const cached = requestsCache.get();
  return Boolean(cached && Date.now() - cached.updatedAt < CACHE_TTL_MS);
};

export const getCachedMutationRequests = () => requestsCache.get()?.data ?? null;

export const setCachedMutationRequests = (data: MutationRequestCollection) => {
  requestsCache.set({ data, updatedAt: Date.now() });
  return data;
};

export const invalidateMutationRequestsCache = () => {
  requestsCache.clear();
};

export const fetchMutationRequests = async (force = false): Promise<MutationRequestCollection> => {
  const cached = requestsCache.get();
  if (!force && cached && Date.now() - cached.updatedAt < CACHE_TTL_MS) return cached.data;

  return loadRequestsOnce(async () => {
    const { data } = await http.get(API_PATH);
    return setCachedMutationRequests(toMutationRequestCollection(normalizeMutationRequests(data)));
  });
};

const removeRequestFromCollection = (
  collection: MutationRequestCollection,
  id: EntityId
): MutationRequestCollection => ({
  pending: collection.pending.filter((request) => request.id !== id),
  history: collection.history.filter((request) => request.id !== id),
});

export const optimisticallyRemoveMutationRequest = (id: EntityId) => {
  const previous = requestsCache.get()?.data ?? null;
  if (previous) setCachedMutationRequests(removeRequestFromCollection(previous, id));
  return previous;
};

export const restoreMutationRequestsCache = (previous: MutationRequestCollection | null) => {
  if (previous) setCachedMutationRequests(previous);
};

export const reconcileMutationRequestAction = (id: EntityId, action: MutationRequestAction, responseData: unknown) => {
  const current = requestsCache.get()?.data;
  if (!current) return null;

  const all = [...current.pending, ...current.history];
  const existing = all.find((request) => request.id === id);
  if (!existing) return current;

  const responseRequest =
    responseData && typeof responseData === "object" && typeof (responseData as Partial<MutationRequest>).id === "string"
      ? (responseData as MutationRequest)
      : null;

  const nextStatus = action === "approve" ? "APPROVED" : action === "reject" ? "REJECTED" : "PENDING";
  const updated: DecoratedMutationRequest = decorateRequest({
    ...existing,
    ...(responseRequest ?? {}),
    status: responseRequest?.status ?? nextStatus,
  });

  const without = removeRequestFromCollection(current, id);
  const next = toMutationRequestCollection([...without.pending, ...without.history, updated]);
  setCachedMutationRequests(next);
  return next;
};

export const performMutationRequestAction = async (
  id: EntityId,
  action: MutationRequestAction
): Promise<unknown> => {
  const { data } = await http.patch(`${API_PATH}/${encodeURIComponent(id)}/${action}`, {});
  reconcileMutationRequestAction(id, action, data);
  return data;
};

export const deleteMutationRequest = async (id: EntityId): Promise<unknown> => {
  const { data } = await http.delete(`${API_PATH}/${encodeURIComponent(id)}`);
  optimisticallyRemoveMutationRequest(id);
  return data;
};

const entityPath = (kind: EntityLookupKind, id: EntityId) => {
  switch (kind) {
    case "user":
      return `/admin/users/${encodeURIComponent(id)}`;
    case "parent":
      return `/parents/${encodeURIComponent(id)}`;
    case "child":
      return `/children/${encodeURIComponent(id)}`;
    case "family":
      return `/families/${encodeURIComponent(id)}`;
  }
};

function toSummary(kind: EntityLookupKind, id: EntityId, data: unknown): LookupEntry {
  if (kind === "user") return userSummaryFromResponse(id, data);
  if (kind === "parent") return parentSummaryFromResponse(id, data);
  if (kind === "child") return childSummaryFromResponse(id, data);
  return familySummaryFromResponse(id, data);
}

export const getCachedLookupMap = <TEntry extends LookupEntry>(kind: EntityLookupKind): LookupMap<TEntry> =>
  new Map(entityCaches[kind] as Map<EntityId, TEntry>);

export const fetchLookupEntry = async <TKind extends EntityLookupKind>(
  kind: TKind,
  id: EntityId
): Promise<(typeof entityCaches)[TKind] extends Map<EntityId, infer TEntry> ? TEntry : LookupEntry> => {
  type Entry = (typeof entityCaches)[TKind] extends Map<EntityId, infer TEntry> ? TEntry : LookupEntry;
  const cache = entityCaches[kind] as Map<EntityId, Entry>;
  const pending = entityRequests[kind] as Map<EntityId, Promise<Entry>>;
  const cached = cache.get(id);
  if (cached) return cached;
  const active = pending.get(id);
  if (active) return active;

  const request = http
    .get(entityPath(kind, id))
    .then(({ data }) => {
      const entry = toSummary(kind, id, data) as Entry;
      cache.set(id, entry);
      return entry;
    })
    .catch(() => {
      const fallback = fallbackLookupEntry(kind, id) as Entry;
      cache.set(id, fallback);
      return fallback;
    })
    .finally(() => {
      pending.delete(id);
    });

  pending.set(id, request);
  return request;
};

export const clearMutationRequestCaches = () => {
  requestsCache.clear();
  entityCaches.user.clear();
  entityCaches.parent.clear();
  entityCaches.child.clear();
  entityCaches.family.clear();
};
